import { useQuery } from '@vue/apollo-composable';
import { computed, toValue, type MaybeRef } from 'vue';

import {
  GetClientMessengerConversationsDocument,
  GetMessengerClientNextBookingDocument,
  type GetMessengerClientNextBookingQuery,
} from '@shared/api/graphql/gql/graphql';

import type { MessengerConversation } from '../model/types';

/** Ближайшая запись клиента (codegen-тип поля, без ручных надстроек). */
export type MessengerClientNextBooking = NonNullable<GetMessengerClientNextBookingQuery['messengerClientNextBooking']>;

/**
 * Query переписок конкретного клиента (GraphQL/Apollo).
 * Возвращает { data } с MessengerConversation[] — сигнатура для ClientPage.
 * Типы — из codegen, без ручных мапперов (id как есть).
 */
export const useClientConversationsQuery = (clientId: MaybeRef<string>) => {
  const { result } = useQuery(
    GetClientMessengerConversationsDocument,
    () => ({ clientId: toValue(clientId) }),
    () => ({ enabled: !!toValue(clientId) }),
  );

  const data = computed<MessengerConversation[]>(() => result.value?.clientMessengerConversations ?? []);

  return { data };
};

/**
 * Ближайшая предстоящая запись клиента для карточки мессенджера.
 * Ленивый по client_id и внешнему module-гейту, отдельный запрос — вне списка групп.
 */
export const useMessengerClientNextBookingQuery = (
  clientId: MaybeRef<string | null | undefined>,
  enabled: MaybeRef<boolean> = true,
) => {
  const { result, loading } = useQuery(
    GetMessengerClientNextBookingDocument,
    () => ({ clientId: toValue(clientId) ?? '' }),
    () => ({ enabled: !!toValue(clientId) && toValue(enabled) }),
  );

  const nextBooking = computed<MessengerClientNextBooking | null>(
    () => result.value?.messengerClientNextBooking ?? null,
  );

  return { nextBooking, loading };
};
